import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { getFileFromUser } from '@weblite/file'
import { RoomTypes } from '@weblite/ts-action/room'
import { toggleEmojiPicker } from 'weblite-web-emoji'
import { SendIcon } from '$common/icons/send'
import { EmojiIcon } from '$common/icons/emoji'
import { EditTickIcon } from '$common/icons/edit-tick-icon'
import { PlusRoundedIcon } from '$common/icons/send-field-plus'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { useRoomIsEducational, useRoomIsMookeb } from '$logic/hall/react-hook'
import { getOpenedRoomId, useOpenedRoomPermissions } from '$logic/opened-room'
import { sendNewMessage } from '$logic/message'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { SEND_FIELD_BORDER_TOP_COLOR } from '$comp/_app/components/chat/components/conf'
import { RecordButton } from '../record-button'
import { TextEditor } from '../text-editor'
import { SendMessageButton } from '../send-message-button'
import { RecorderStatus, SendFieldAction } from '../../send-field.type'
import { showChatToolboxModal } from '$comp/_app/components/chat/components/footer/components/send-field/components/chat-toolbox-modal/chat-toolbox-modal.api'
import { changeFileTypeToValidFileOrAudioMessageType } from '../../helper'
import { SendFieldIconButton } from '../icon-button'
import { MentionMenu } from '../mention-menu'
import { t } from '$i18n'
import { validateMessageFileSizeAndShowError } from '$logic/message/helper'

interface Props {
  action: SendFieldAction
  mentionIsDisabled: boolean
  recorderState: RecorderStatus
  submitSendField: () => void
  setRecorderState: (state: RecorderStatus) => unknown
  setSelectedFile: (file: File) => unknown
  setMentionQuery: Dispatch<SetStateAction<string | undefined>>
  hasFile: boolean
  imageCaptionMode: boolean
  mentionQuery?: string
  onEditorTextChange: (value: string) => unknown
  bottomOffset: number
  isHidden: boolean
  roomType: RoomTypes
}

export function TextPreview({
  action,
  mentionIsDisabled,
  recorderState,
  setRecorderState,
  submitSendField,
  onEditorTextChange,
  setSelectedFile,
  setMentionQuery,
  hasFile,
  imageCaptionMode,
  mentionQuery,
  bottomOffset,
  isHidden,
  roomType,
}: Props) {
  const isDesktopSize = useIsDesktopSize()
  const [hasMessage, setHasMessage] = useState(false)

  const { openedRoomPermissions } = useOpenedRoomPermissions()
  const canSendMedia = openedRoomPermissions?.sendMedia

  const textMode =
    hasMessage || hasFile || action.type === 'EDIT' || action.type === 'FORWARD'

  const handleSelectedFile = useCallback(async (files: File | File[]) => {
    if (Array.isArray(files) && files.length > 1) {
      const roomId = getOpenedRoomId()
      files.forEach(file => {
        if (validateMessageFileSizeAndShowError(file))
          sendNewMessage(
            changeFileTypeToValidFileOrAudioMessageType(file),
            undefined,
            undefined,
            roomId,
            roomType,
          )
      })
    } else {
      const file = Array.isArray(files) ? files[0] : files
      if (!file) return
      const newFile = changeFileTypeToValidFileOrAudioMessageType(file)
      if (validateMessageFileSizeAndShowError(newFile)) setSelectedFile(newFile)
    }
  }, [])

  const handleSelectedMedia = useCallback((files: File | File[]) => {
    if (Array.isArray(files) && files.length > 1) {
      const roomId = getOpenedRoomId()
      files.forEach(file => {
        if (validateMessageFileSizeAndShowError(file))
          sendNewMessage(file, undefined, undefined, roomId, roomType)
      })
    } else {
      const newFile = Array.isArray(files) ? files[0] : files
      if (validateMessageFileSizeAndShowError(newFile)) setSelectedFile(newFile)
    }
  }, [])

  const roomId = getOpenedRoomId()
  const { roomIsMookeb } = useRoomIsMookeb(roomId)
  const { roomIsEducational } = useRoomIsEducational(roomId)

  return (
    <div
      style={{
        zIndex: 1,
        display: isHidden ? 'none' : 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        position: imageCaptionMode ? 'absolute' : 'relative',
        bottom: imageCaptionMode ? bottomOffset : 'unset',
        width: imageCaptionMode ? '100%' : 'unset',
        boxSizing: 'border-box',
        backgroundColor: imageCaptionMode ? 'unset' : 'white',

        ...(isDesktopSize &&
          imageCaptionMode && {
            position: 'fixed',
            left: 0,
            right: 0,
            zIndex: 2,
          }),
      }}
      onPaste={e => {
        if (e.clipboardData.files.length > 0)
          setSelectedFile(e.clipboardData.files[0])
      }}
    >
      <div
        style={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          backgroundColor: imageCaptionMode ? 'black' : 'unset',
          opacity: '0.5',
          zIndex: -1,
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {imageCaptionMode && mentionQuery !== undefined && (
          <MentionMenu imageCaptionMode query={mentionQuery} />
        )}

        <div
          style={{
            display: 'flex',
            width: '100%',
            padding: '3px 8px',
            backgroundColor: imageCaptionMode ? '#00000080' : 'unset',
            borderTop: imageCaptionMode
              ? 'unset'
              : `1px solid ${SEND_FIELD_BORDER_TOP_COLOR}`,
            justifyContent: 'space-between',
            alignItems: 'center',
            boxSizing: 'border-box',
            bottom: imageCaptionMode ? 0 : 'unset',
          }}
        >
          {textMode && (
            <SendMessageButton
              onSend={submitSendField}
              disabled={!hasMessage && !hasFile && action.type === 'EDIT'}
            >
              {action.type === 'EDIT' ? <EditTickIcon /> : <SendIcon />}
            </SendMessageButton>
          )}

          {((!textMode && canSendMedia) || recorderState === 'PREVIEW') && (
            <RecordButton
              state={recorderState}
              setRecorderState={setRecorderState}
            />
          )}

          <TextEditor
            action={action}
            mentionIsDisabled={mentionIsDisabled}
            darkMode={imageCaptionMode}
            placeholder={
              imageCaptionMode
                ? t('chat.sendFieldCaption')
                : t('chat.sendField')
            }
            setHasMessage={setHasMessage}
            onContentChange={onEditorTextChange}
            setMentionQuery={setMentionQuery}
            onSubmit={submitSendField}
            onPasteFile={setSelectedFile}
          />

          {!isPhoneOrTablet && (
            <SendFieldIconButton
              onClick={e => {
                e.stopPropagation()
                toggleEmojiPicker()
              }}
            >
              <EmojiIcon />
            </SendFieldIconButton>
          )}

          {((!textMode && canSendMedia) || recorderState === 'PREVIEW') && (
            <SendFieldIconButton
              onClick={() =>
                showChatToolboxModal({
                  handleUploadMedia: () =>
                    getFileFromUser(
                      'image/*, video/*',
                      handleSelectedMedia,
                      action.type === 'NEW_MESSAGE' && !hasMessage,
                    ),
                  handleUploadVideo: () =>
                    getFileFromUser(
                      'video/*',
                      handleSelectedMedia,
                      action.type === 'NEW_MESSAGE' && !hasMessage,
                    ),
                  handleUploadFile: () =>
                    getFileFromUser(
                      '*',
                      handleSelectedFile,
                      action.type === 'NEW_MESSAGE' && !hasMessage,
                    ),
                  roomIsMokeb: Boolean(roomIsMookeb),
                  roomIsEducational: Boolean(roomIsEducational),
                })
              }
            >
              <PlusRoundedIcon />
            </SendFieldIconButton>
          )}
        </div>
      </div>
    </div>
  )
}
